import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderTree, Building2, Network, Users, Plus, Search,
  Edit2, Trash2, ChevronRight, ChevronDown, AlertCircle,
  Key, Copy, CheckCircle
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { getCoachIdForUser } from '../../../lib/coach-teams';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

interface Project {
  id: string;
  name: string;
  description: string | null;
  level: 'company' | 'program' | 'cohort';
  parentId: string | null;
  isActive: boolean;
  children: Project[];
  teams: TeamSummary[];
}

interface TeamSummary {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  assessmentCode: string | null;
  maxUses: number | null;
  useCount: number;
  projectId: string | null;
}

type ModalMode = 'company' | 'program' | 'team' | null;

export function CoachProjectsTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [unassignedTeams, setUnassignedTeams] = useState<TeamSummary[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [parentContext, setParentContext] = useState<{ id: string; name: string } | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '', maxUses: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const cId = await getCoachIdForUser(user.id);
        if (!cId) { navigate('/tr/coach'); return; }
        setCoachId(cId);
        await loadData(cId);
      } catch (err) {
        console.error('Error initializing:', err);
        setError('Projeler yuklenemedi');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadData = async (cId: string) => {
    const { data: projectsData, error: pErr } = await supabase
      .from('projects')
      .select('*')
      .eq('coach_id', cId)
      .eq('is_active', true)
      .order('name');

    if (pErr) throw pErr;

    const { data: teamsData, error: tErr } = await supabase
      .from('teams')
      .select('*')
      .eq('coach_id', cId)
      .eq('is_active', true)
      .order('name');

    if (tErr) throw tErr;

    const teamIds = (teamsData || []).map(t => t.id);
    const countMap: Record<string, number> = {};
    const codeMap: Record<string, string> = {};
    const codeMaxUsesMap: Record<string, number | null> = {};
    const codeUseCountMap: Record<string, number> = {};

    if (teamIds.length > 0) {
      const { data: memberCounts } = await supabase
        .from('team_members')
        .select('team_id')
        .in('team_id', teamIds);

      (memberCounts || []).forEach(m => {
        countMap[m.team_id] = (countMap[m.team_id] || 0) + 1;
      });

      const { data: codes } = await supabase
        .from('assessment_codes')
        .select('code, team_id, max_uses, use_count')
        .in('team_id', teamIds);

      (codes || []).forEach(c => {
        if (c.team_id && !codeMap[c.team_id]) codeMap[c.team_id] = c.code;
        if (c.team_id) {
          codeMaxUsesMap[c.team_id] = c.max_uses;
          codeUseCountMap[c.team_id] = c.use_count ?? 0;
        }
      });
    }

    const teamSummaries: TeamSummary[] = (teamsData || []).map(t => ({
      id: t.id,
      name: t.name,
      description: t.description,
      memberCount: countMap[t.id] || 0,
      assessmentCode: codeMap[t.id] || null,
      maxUses: codeMaxUsesMap[t.id] ?? null,
      useCount: codeUseCountMap[t.id] ?? 0,
      projectId: t.project_id,
    }));

    const rawProjects = projectsData || [];
    const projectMap = new Map<string, Project>();

    rawProjects.forEach(p => {
      projectMap.set(p.id, {
        id: p.id,
        name: p.name,
        description: p.description,
        level: p.level as 'company' | 'program' | 'cohort',
        parentId: p.parent_id,
        isActive: p.is_active,
        children: [],
        teams: [],
      });
    });

    teamSummaries.forEach(t => {
      if (t.projectId && projectMap.has(t.projectId)) {
        projectMap.get(t.projectId)!.teams.push(t);
      }
    });

    rawProjects.forEach(p => {
      if (p.parent_id && projectMap.has(p.parent_id)) {
        projectMap.get(p.parent_id)!.children.push(projectMap.get(p.id)!);
      }
    });

    const topLevel = rawProjects
      .filter(p => !p.parent_id)
      .map(p => projectMap.get(p.id)!)
      .filter(Boolean);

    setProjects(topLevel);
    setUnassignedTeams(teamSummaries.filter(t => !t.projectId));
  };

  const toggleExpand = (id: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreateModal = (mode: ModalMode, parent?: { id: string; name: string }) => {
    setModalMode(mode);
    setParentContext(parent || null);
    setEditingId(null);
    setFormData({ name: '', description: '', maxUses: '' });
  };

  const openEditProject = (project: Project) => {
    setModalMode(project.level === 'company' ? 'company' : 'program');
    setEditingId(project.id);
    setParentContext(null);
    setFormData({ name: project.name, description: project.description || '', maxUses: '' });
  };

  const openEditTeam = (team: TeamSummary) => {
    setModalMode('team');
    setEditingId(team.id);
    setParentContext(null);
    setFormData({ name: team.name, description: team.description || '', maxUses: '' });
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setParentContext(null);
    setFormData({ name: '', description: '', maxUses: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId) return;
    setError(null);
    setIsSubmitting(true);

    try {
      if (modalMode === 'team') {
        if (editingId) {
          const { error: err } = await supabase
            .from('teams')
            .update({
              name: formData.name,
              description: formData.description || null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', editingId);
          if (err) throw err;
        } else {
          const { data: newTeam, error: err } = await supabase
            .from('teams')
            .insert({
              coach_id: coachId,
              name: formData.name,
              description: formData.description || null,
              type: 'team',
              project_id: parentContext?.id || null,
            })
            .select('id')
            .single();

          if (err) throw err;

          if (newTeam) {
            const code = 'HPOS' + Math.random().toString(36).substring(2, 8).toUpperCase();
            const maxUses = formData.maxUses ? parseInt(formData.maxUses) : null;
            await supabase
              .from('assessment_codes')
              .insert({
                code,
                coach_id: coachId,
                team_id: newTeam.id,
                is_used: false,
                max_uses: maxUses,
              });
          }
        }
      } else {
        const level = modalMode as 'company' | 'program';
        if (editingId) {
          const { error: err } = await supabase
            .from('projects')
            .update({
              name: formData.name,
              description: formData.description || null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', editingId);
          if (err) throw err;
        } else {
          const { error: err } = await supabase
            .from('projects')
            .insert({
              coach_id: coachId,
              name: formData.name,
              description: formData.description || null,
              level,
              parent_id: parentContext?.id || null,
            });
          if (err) throw err;
        }
      }

      await loadData(coachId);
      closeModal();
    } catch (err: any) {
      console.error('Error saving:', err);
      setError(err.message || 'Kaydetme basarisiz oldu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async (projectId: string, name: string) => {
    if (!confirm(`"${name}" silinsin mi? Bu islem icindeki tum programlari ve takimlari da silecektir.`)) return;
    if (!coachId) return;

    try {
      const { error: err } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);
      if (err) throw err;
      await loadData(coachId);
    } catch (err: any) {
      setError(err.message || 'Silme basarisiz oldu');
    }
  };

  const handleDeleteTeam = async (teamId: string, name: string) => {
    if (!confirm(`"${name}" takimi silinsin mi? Bu islem tum uye iliskilerini kaldiacaktir.`)) return;
    if (!coachId) return;

    try {
      const { error: err } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);
      if (err) throw err;
      await loadData(coachId);
    } catch (err: any) {
      setError(err.message || 'Takim silme basarisiz oldu');
    }
  };

  const copyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getModalTitle = () => {
    const action = editingId ? 'Duzenle' : 'Olustur';
    switch (modalMode) {
      case 'company': return `Musteri ${action}`;
      case 'program': return `Program ${action}`;
      case 'team': return `Takim ${action}`;
      default: return '';
    }
  };

  const getPlaceholder = () => {
    switch (modalMode) {
      case 'company': return 'orn. Acme Sirketi';
      case 'program': return 'orn. Liderlik Gelistirme 2026';
      case 'team': return 'orn. Yonetim Takimi Q1';
      default: return '';
    }
  };

  const filteredProjects = searchQuery
    ? projects.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.children.some(c =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.teams.some(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
        ) ||
        p.teams.some(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : projects;

  const filteredUnassigned = searchQuery
    ? unassignedTeams.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : unassignedTeams;

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Projeler yukleniyor..." />;
  }

  const isEmpty = projects.length === 0 && unassignedTeams.length === 0;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-warm-500 rounded-xl shadow-soft">
                <FolderTree className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-chocolate-900">Projeler</h1>
                <p className="text-sm text-chocolate-500">
                  Musterilerinizi, programlarinizi ve takimlarinizi duzenleyin
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openCreateModal('team')}
                className="freud-btn-secondary flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Yeni Takim
              </button>
              <button
                onClick={() => openCreateModal('company')}
                className="freud-btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Yeni Musteri
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-coral-500 hover:text-coral-700 font-bold">&times;</button>
          </div>
        )}

        {!isEmpty && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Musteri, program veya takim ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
          </div>
        )}

        {isEmpty ? (
          <EmptyStateTR
            onCreateClient={() => openCreateModal('company')}
            onCreateTeam={() => openCreateModal('team')}
          />
        ) : (
          <div className="space-y-3">
            {filteredProjects.map(project => (
              <ProjectNodeTR
                key={project.id}
                project={project}
                expandedNodes={expandedNodes}
                onToggle={toggleExpand}
                onEditProject={openEditProject}
                onEditTeam={openEditTeam}
                onDeleteProject={handleDeleteProject}
                onDeleteTeam={handleDeleteTeam}
                onCreateChild={openCreateModal}
                onViewTeam={(teamId) => navigate(`/tr/coach/teams/${teamId}`)}
                onCopyCode={copyCode}
                copiedCode={copiedCode}
                depth={0}
              />
            ))}

            {filteredUnassigned.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-chocolate-500 uppercase tracking-wide mb-3 px-1">
                  Bagimsiz Takimlar
                </h3>
                <div className="freud-card-elevated overflow-hidden divide-y divide-cream-200">
                  {filteredUnassigned.map(team => (
                    <TeamRowTR
                      key={team.id}
                      team={team}
                      onView={() => navigate(`/tr/coach/teams/${team.id}`)}
                      onEdit={() => openEditTeam(team)}
                      onDelete={() => handleDeleteTeam(team.id, team.name)}
                      onCopyCode={copyCode}
                      copiedCode={copiedCode}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {modalMode && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <h2 className="text-xl font-bold text-chocolate-900 mb-2">{getModalTitle()}</h2>
            {parentContext && (
              <p className="text-sm text-chocolate-500 mb-4">
                Ust: <span className="font-medium text-chocolate-700">{parentContext.name}</span>
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Ad</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder={getPlaceholder()}
                  className="freud-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Aciklama (istege bagli)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kisa aciklama..."
                  rows={3}
                  className="freud-input resize-none"
                />
              </div>
              {modalMode === 'team' && !editingId && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">Maksimum Uye (istege bagli)</label>
                    <input
                      type="number"
                      value={formData.maxUses}
                      onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                      min="1"
                      placeholder="Sinirsiz"
                      className="freud-input"
                    />
                    <p className="text-xs text-chocolate-400 mt-1">
                      Bu takimin koduyla kac kisinin kayit olabilecegini sinirlayin
                    </p>
                  </div>
                  <div className="bg-sage-50 border border-sage-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-sage-700 text-sm">
                      <Key className="w-4 h-4" />
                      <span>Bu takim icin otomatik olarak bir degerlendirme kodu olusturulacaktir</span>
                    </div>
                  </div>
                </>
              )}
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="freud-btn-secondary flex-1" disabled={isSubmitting}>
                  Iptal
                </button>
                <button type="submit" className="freud-btn-primary flex-1" disabled={isSubmitting}>
                  {isSubmitting ? 'Kaydediliyor...' : (editingId ? 'Degisiklikleri Kaydet' : 'Olustur')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function EmptyStateTR({ onCreateClient, onCreateTeam }: { onCreateClient: () => void; onCreateTeam: () => void }) {
  return (
    <div className="freud-card-elevated text-center py-16 px-8">
      <FolderTree className="w-16 h-16 text-cream-400 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-chocolate-700 mb-2">Kocluk calismanizi duzenleyin</h3>
      <p className="text-chocolate-500 mb-8 max-w-md mx-auto">
        Programlarinizi ve takimlarinizi gruplamak icin bir musteri olusturun. Her takim, katilimcilarin kayit olmak icin kullandigi kendi degerlendirme kodunu alir.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={onCreateClient} className="freud-btn-primary inline-flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Ilk Musterinizi Olusturun
        </button>
        <button onClick={onCreateTeam} className="freud-btn-secondary inline-flex items-center gap-2">
          <Users className="w-5 h-5" />
          Bagimsiz Takim Olusturun
        </button>
      </div>
    </div>
  );
}

function ProjectNodeTR({
  project,
  expandedNodes,
  onToggle,
  onEditProject,
  onEditTeam,
  onDeleteProject,
  onDeleteTeam,
  onCreateChild,
  onViewTeam,
  onCopyCode,
  copiedCode,
  depth,
}: {
  project: Project;
  expandedNodes: Set<string>;
  onToggle: (id: string) => void;
  onEditProject: (p: Project) => void;
  onEditTeam: (t: TeamSummary) => void;
  onDeleteProject: (id: string, name: string) => void;
  onDeleteTeam: (id: string, name: string) => void;
  onCreateChild: (mode: ModalMode, parent?: { id: string; name: string }) => void;
  onViewTeam: (teamId: string) => void;
  onCopyCode: (code: string) => void;
  copiedCode: string | null;
  depth: number;
}) {
  const isExpanded = expandedNodes.has(project.id);
  const hasChildren = project.children.length > 0 || project.teams.length > 0;
  const childLabel: ModalMode = project.level === 'company' ? 'program' : 'team';
  const totalTeams = project.teams.length + project.children.reduce((sum, c) => sum + c.teams.length, 0);

  const levelConfig: Record<string, { bg: string; icon: typeof Building2; iconColor: string; border: string; label: string }> = {
    company: { bg: 'bg-warm-100', icon: Building2, iconColor: 'text-warm-600', border: 'border-warm-200', label: 'musteri' },
    program: { bg: 'bg-sage-50', icon: Network, iconColor: 'text-sage-600', border: 'border-sage-200', label: 'program' },
    cohort: { bg: 'bg-sage-50', icon: Network, iconColor: 'text-sage-600', border: 'border-sage-200', label: 'program' },
  };

  const config = levelConfig[project.level] || levelConfig.program;
  const Icon = config.icon;

  return (
    <div className={depth > 0 ? 'ml-6' : ''}>
      <div className={`freud-card-elevated border ${config.border} overflow-hidden`}>
        <div
          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-cream-50 transition-colors"
          onClick={() => hasChildren && onToggle(project.id)}
        >
          <button
            className={`p-1 rounded transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'} ${!hasChildren ? 'invisible' : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggle(project.id); }}
          >
            <ChevronDown className="w-4 h-4 text-chocolate-400" />
          </button>

          <div className={`p-2 rounded-lg ${config.bg}`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-chocolate-900 truncate">{project.name}</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-cream-200 text-chocolate-500">
                {config.label}
              </span>
            </div>
            {project.description && (
              <p className="text-sm text-chocolate-500 truncate">{project.description}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {totalTeams > 0 && (
              <span className="text-sm text-chocolate-500">
                {totalTeams} takim
              </span>
            )}
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); onCreateChild(childLabel, { id: project.id, name: project.name }); }}
                className="p-1.5 text-sage-600 hover:bg-sage-100 rounded-lg transition-colors"
                title={`${childLabel === 'program' ? 'Program' : 'Takim'} ekle`}
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onEditProject(project); }}
                className="p-1.5 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
                title="Duzenle"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteProject(project.id, project.name); }}
                className="p-1.5 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                title="Sil"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && hasChildren && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-cream-200 bg-cream-50/50 py-2">
                {project.children.map(child => (
                  <div key={child.id} className="px-2 py-1">
                    <ProjectNodeTR
                      project={child}
                      expandedNodes={expandedNodes}
                      onToggle={onToggle}
                      onEditProject={onEditProject}
                      onEditTeam={onEditTeam}
                      onDeleteProject={onDeleteProject}
                      onDeleteTeam={onDeleteTeam}
                      onCreateChild={onCreateChild}
                      onViewTeam={onViewTeam}
                      onCopyCode={onCopyCode}
                      copiedCode={copiedCode}
                      depth={depth + 1}
                    />
                  </div>
                ))}

                {project.teams.map(team => (
                  <div key={team.id} className="ml-10">
                    <TeamRowTR
                      team={team}
                      onView={() => onViewTeam(team.id)}
                      onEdit={() => onEditTeam(team)}
                      onDelete={() => onDeleteTeam(team.id, team.name)}
                      onCopyCode={onCopyCode}
                      copiedCode={copiedCode}
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function TeamRowTR({
  team,
  onView,
  onEdit,
  onDelete,
  onCopyCode,
  copiedCode,
}: {
  team: TeamSummary;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopyCode: (code: string) => void;
  copiedCode: string | null;
}) {
  return (
    <div
      className="flex items-center justify-between px-4 py-3 hover:bg-cream-50 transition-colors cursor-pointer group"
      onClick={onView}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="p-2 rounded-lg bg-sage-100">
          <Users className="w-4 h-4 text-sage-600" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="font-medium text-chocolate-900 truncate block">{team.name}</span>
          {team.description && (
            <p className="text-xs text-chocolate-400 truncate">{team.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-sm text-chocolate-500">
          <Users className="w-3.5 h-3.5" />
          <span>{team.memberCount}</span>
        </div>

        {team.assessmentCode && (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onCopyCode(team.assessmentCode!); }}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-cream-200 hover:bg-cream-300 rounded-md transition-colors"
              title="Degerlendirme kodunu kopyala"
            >
              <Key className="w-3 h-3 text-chocolate-500" />
              <code className="text-xs font-mono text-chocolate-700">{team.assessmentCode}</code>
              {copiedCode === team.assessmentCode ? (
                <CheckCircle className="w-3 h-3 text-sage-600" />
              ) : (
                <Copy className="w-3 h-3 text-chocolate-400" />
              )}
            </button>
            {team.maxUses !== null && (
              <span className="text-xs text-chocolate-400">
                {team.useCount}/{team.maxUses}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1.5 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
            title="Takimi duzenle"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
            title="Takimi sil"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        <ChevronRight className="w-4 h-4 text-chocolate-300 group-hover:text-chocolate-500 transition-colors" />
      </div>
    </div>
  );
}
